import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addDays, addMonths, startOfWeek } from 'date-fns';
import { useAuth } from './AuthContext';
import './WeeklyAvailabilityPage.css';

const API_URL = 'https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod';

function WeeklyAvailabilityPage() {
  const { professionalId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const selectedServiceId = location.state?.serviceId || '';

  const [loading, setLoading] = useState(true);
  const [availabilities, setAvailabilities] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filteredSlots, setFilteredSlots] = useState([]);
  const [highlightedDates, setHighlightedDates] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [error, setError] = useState('');

  const [waitlistPromptVisible, setWaitlistPromptVisible] = useState(false);
  const [pendingWaitlistPayload, setPendingWaitlistPayload] = useState(null);
  const [confirmInfosChecked, setConfirmInfosChecked] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
const [editedUserInfos, setEditedUserInfos] = useState({
  first_name: user?.first_name || '',
  last_name: user?.last_name || '',
  email: user?.email || '',
  phone_number: user?.phone_number || '',
  birth_date: user?.birth_date || '',
});

  useEffect(() => {
    console.log('🔍 User context:', user);
  }, [user]);

  useEffect(() => {
    if (!selectedServiceId) return;
    setLoading(true);
    axios
      .get(`${API_URL}/professionals/${professionalId}/weekly-availabilities`, {
        params: { service_id: selectedServiceId },
      })
      .then(res => {
        setAvailabilities(res.data);
        computeHighlightedDates(res.data);
      })
      .catch(err => {
        console.error('Erreur fetching availabilities:', err);
        setError("Impossible de charger les disponibilités. Réessayez.");
      })
      .finally(() => setLoading(false));
  }, [professionalId, selectedServiceId]);

  const computeHighlightedDates = (data) => {
    const highlightDates = data
      .map(a => {
        if (!a.date) return null;
        const [year, month, day] = a.date.split('-');
        return new Date(Number(year), Number(month) - 1, Number(day));
      })
      .filter(Boolean);
    setHighlightedDates(highlightDates);
  };

  useEffect(() => {
    setSelectedSlot(null);
    setWaitlistPromptVisible(false);

    if (!selectedDate) {
      setFilteredSlots([]);
      return;
    }

    const iso = selectedDate.toISOString().split('T')[0];
    const slots = availabilities.filter(a => {
      if (a.date) return a.date === iso;

      const baseDate = startOfWeek(new Date(), { weekStartsOn: 1 });
      const offset = a.day_of_week - 1;
      for (let w = 0; w < 8; w++) {
        const possibleDate = addDays(baseDate, w * 7 + offset);
        if (possibleDate.toISOString().split('T')[0] === iso) return true;
      }
      return false;
    });

    setFilteredSlots(slots);
  }, [selectedDate, availabilities]);

  const isDayDisabled = (date) => {
    const iso = date.toISOString().split('T')[0];
    return !availabilities.some(a => a.date === iso);
  };

  const confirmReservation = async () => {
    if (!selectedDate || !selectedSlot) return;
  
    const dateOnly = selectedDate.toISOString().split('T')[0];
  
    // 🔧 Construction d'une vraie date en UTC (très important)
    const toUTCISOString = (dateStr, timeStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hour, minute] = timeStr.split(':').map(Number);
      return new Date(Date.UTC(year, month - 1, day, hour, minute)).toISOString();
    };
  
    const startUtcIso = toUTCISOString(dateOnly, selectedSlot.start_time); // ex: '2025-08-22T14:30:00.000Z'
    const endUtcIso = toUTCISOString(dateOnly, selectedSlot.end_time);
  
    const payload = {
      professionalId,
      serviceId: selectedServiceId,
      date: dateOnly,
      startTime: selectedSlot.start_time,
      endTime: selectedSlot.end_time,
      mode: selectedSlot.mode
    };
  
    console.log('🕓 Créneau sélectionné (local):', selectedSlot.start_time, '-', selectedSlot.end_time);
    console.log('🌐 Start UTC:', startUtcIso);
    console.log('🌐 End UTC:', endUtcIso);
  
    try {
      const res = await axios.get(`${API_URL}/check-availability`, {
        params: {
          professional_id: professionalId,
          service_id: selectedServiceId,
          date: dateOnly,                 
          start_time: selectedSlot.start_time,         
          end_time: selectedSlot.end_time             
        }
      });
  
      if (res.data.available) {
        if (user?.user_id && user?.user_type === 'patient' && user?.email_verified) {
          navigate('/reservation/confirmation', { state: payload });
        } else {
          navigate(`/reservation/${professionalId}`, { state: payload });
        }
      } else {
        setPendingWaitlistPayload(payload);
        setWaitlistPromptVisible(true);
      }
    } catch (err) {
      console.error('❌ Erreur réservation / attente :', err);
      alert('Erreur lors de la réservation / liste d’attente');
    }
  };
  
  return (
    <div className="wap-wrapper">
      <div className="wap-card">
        <div className="wap-header">
          <h2>Sélectionner la date et l’heure</h2>
          <span className="wap-tz">Time Zone: America/Toronto</span>
        </div>
        {showEditModal && (
    <div className="modal-backdrop">
      <div className="modal-content">
        <h5>Modifier mes informations</h5>
  
        <div className="form-group">
          <label>Prénom</label>
          <input className="form-control" value={editedUserInfos.first_name}
            onChange={(e) => setEditedUserInfos({ ...editedUserInfos, first_name: e.target.value })} />
        </div>
  
        <div className="form-group">
          <label>Nom</label>
          <input className="form-control" value={editedUserInfos.last_name}
            onChange={(e) => setEditedUserInfos({ ...editedUserInfos, last_name: e.target.value })} />
        </div>
  
        <div className="form-group">
          <label>Email</label>
          <input className="form-control" value={editedUserInfos.email}
            onChange={(e) => setEditedUserInfos({ ...editedUserInfos, email: e.target.value })} />
        </div>
  
        <div className="form-group">
          <label>Téléphone</label>
          <input className="form-control" value={editedUserInfos.phone_number}
            onChange={(e) => setEditedUserInfos({ ...editedUserInfos, phone_number: e.target.value })} />
        </div>
  
        <div className="form-group">
          <label>Date de naissance</label>
          <input type="date" className="form-control" value={editedUserInfos.birth_date}
            onChange={(e) => setEditedUserInfos({ ...editedUserInfos, birth_date: e.target.value })} />
        </div>
  
        <div className="mt-3" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={() => {
            // Tu peux ici faire un appel API si tu veux aussi sauvegarder ça dans la base
            setShowEditModal(false);
          }}>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  )}
        {loading ? (
          <div className="text-center py-5">Chargement…</div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <div className="wap-content">
            <div className="wap-calendar">
              <DatePicker
                selected={selectedDate}
                onChange={date => setSelectedDate(date)}
                inline
                highlightDates={[
                  { 'react-datepicker__day--highlighted-custom': highlightedDates },
                  { 'react-datepicker__day--today-custom': [new Date()] }
                ]}
                minDate={new Date()}
                maxDate={addMonths(new Date(), 2)}
                filterDate={date => !isDayDisabled(date)}
              />
            </div>

            <div className="wap-slots">
              <h5>Créneaux horaires disponibles</h5>

              {!selectedDate && (
                <p className="text-muted mt-3">Veuillez d’abord sélectionner une date.</p>
              )}

              {selectedDate && filteredSlots.length === 0 && (
                <>
                  <p className="text-muted mt-3">Aucun créneau disponible pour cette date.</p>
                  <button
                    className="btn btn-waiting"
                    onClick={() =>
                      navigate('/waiting-list', {
                        state: { serviceId: selectedServiceId, professionalId }
                      })
                    }
                  >
                    🔔 S’inscrire à la liste d’attente
                  </button>
                </>
              )}

              {selectedDate && filteredSlots.length > 0 && (
                <div className="slot-grid">
                  {filteredSlots.map((slot, idx) => {
                    const active = selectedSlot?.start_time === slot.start_time && selectedSlot?.mode === slot.mode;
                    return (
                      <button
                        key={idx}
                        className={`slot-btn ${active ? 'active' : ''}`}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        <div className="slot-time">{slot.start_time}</div>
                        <div className="slot-mode">{slot.mode}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              {waitlistPromptVisible && (
                <div className="waitlist-alert">
                  <p className="text-danger">❌ Ce créneau est déjà réservé.</p>
                  <p>Souhaitez-vous vous inscrire sur la liste d’attente ?</p>

                  <div className="patient-infos">
                  <h5>Vos informations :</h5>
                  <ul>
                    <li><strong>Nom :</strong> {editedUserInfos.first_name} {editedUserInfos.last_name}</li>
                    <li><strong>Email :</strong> {editedUserInfos.email}</li>
                    <li><strong>Téléphone :</strong> {editedUserInfos.phone_number || '—'}</li>
                    <li><strong>Date de naissance :</strong> {editedUserInfos.birth_date || '—'}</li>
                  </ul>
                  <button
                  className="btn btn-link text-decoration-underline"
                  style={{ marginBottom: '10px' }}
                  onClick={() => setShowEditModal(true)}
                >
                  ✏️ Modifier mes informations
                </button>
                </div>

                  <div style={{ marginTop: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={confirmInfosChecked}
                        onChange={(e) => setConfirmInfosChecked(e.target.checked)}
                        style={{ marginRight: '10px' }}
                      />
                      Je confirme que mes informations sont exactes pour l’inscription.
                    </label>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setWaitlistPromptVisible(false);
                        setPendingWaitlistPayload(null);
                        setConfirmInfosChecked(false);
                      }}
                    >
                      Annuler
                    </button>
                    <button
                      className="btn btn-success"
                      disabled={!confirmInfosChecked}
                      onClick={async () => {
                        try {
                          await axios.post(`${API_URL}/waiting-list`, {
                            ...(user?.user_id && user?.user_type === 'patient' && { patient_id: user.user_id }),
                          
                            first_name: editedUserInfos.first_name,
                            last_name: editedUserInfos.last_name,
                            email: editedUserInfos.email,
                            phone_number: editedUserInfos.phone_number,
                            birth_date: editedUserInfos.birth_date,
                            service_id: pendingWaitlistPayload.serviceId,
                            professional_id: professionalId,
                            date: pendingWaitlistPayload.date,
                            start_time: pendingWaitlistPayload.startTime,
                            end_time: pendingWaitlistPayload.endTime,
                            mode: pendingWaitlistPayload.mode
                          });
                          navigate('/patient/waitinglistpage');
                        } catch (err) {
                          console.error('Erreur inscription liste attente:', err);
                          alert('Erreur lors de l’inscription à la liste d’attente.');
                        }
                      }}
                    >
                      Oui, m’inscrire
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="wap-footer">
              <button className="btn btn-ghost" onClick={() => navigate(-1)}>Précédent</button>
              <button
                className="btn btn-primary"
                disabled={!selectedSlot || !selectedDate}
                onClick={confirmReservation}
              >
                Suivant
              </button>
            </div>
          </div> 
        )}
      </div> 
    </div> 
    
  );
}

export default WeeklyAvailabilityPage;